import React, { useCallback, useEffect, useState } from "react";
import { TreeView } from "@mui/x-tree-view";
import styled from "styled-components";

import { fork } from "@formant/core/common/fork";
import { ISqlColumn } from "@formant/core/model/ISqlColumn";
import { ISqlTable } from "@formant/core/model/ISqlTable";
import { primary } from "@formant/ui/component/common/Colors";
import { IconName } from "@formant/ui/component/common/Icon";

import { analyticsStore } from "../../stores";
import { usePopup } from "../../utils/hooks/usePopupHooks";
import { IconButton } from "../common/IconButton";
import { Popup } from "../common/Popup";
import { CopyableTreeNode } from "./CopyableTreeNode";

export function SqlTableViewer() {
    const [tables, setTables] = useState<ISqlTable[] | undefined>(undefined);

    const getTables = useCallback(async () => {
        if (tables) {
            return;
        }
        const items = await analyticsStore.listSqlTables();
        if (items) {
            setTables(items);
        }
    }, []);

    useEffect(() => {
        fork(getTables());
    }, []);

    if (!tables) {
        return null;
    }

    const columnToDataType = (
        tableName: string,
        column: ISqlColumn,
    ): string => {
        if (column.name === "tags") {
            return "Map(String, String)";
        }
        if (tableName === "query_numeric_set" && column.name === "value") {
            return "Map(String,Tuple(value Float64, unit String))";
        }
        if (tableName === "query_bitset" && column.name === "value") {
            return "Map(String, Bool)";
        }
        return column.dataType;
    };

    const datapointTables = tables.filter(_ =>
        [...snowflakeTables.streams, ...clickhouseTables.streams].some(s =>
            _.name.includes(s),
        ),
    );
    const eventTables = tables.filter(_ =>
        [...snowflakeTables.events, ...clickhouseTables.events].some(s =>
            _.name.includes(s),
        ),
    );

    const entityTables = tables.filter(_ =>
        [...snowflakeTables.entities, ...clickhouseTables.entities].some(s =>
            _.name.includes(s),
        ),
    );

    const tableToNode = (t: ISqlTable) => (
        <CopyableTreeNode
            itemId={t.name}
            key={t.name}
            label={t.name}
            badge={String(t.columns.length) + " columns"}
        >
            {t.columns.map(column => (
                <CopyableTreeNode
                    itemId={t.name + column.dataType + column.name}
                    key={t.name + column.dataType + column.name}
                    label={column.name}
                    badge={columnToDataType(t.name, column)}
                    icon={columnToIcon(column.dataType)}
                    copy={true}
                />
            ))}
        </CopyableTreeNode>
    );

    return (
        <TreeViewStyled>
            <CopyableTreeNode
                itemId="streams"
                key="streams"
                label="Datapoint Tables"
                badge={String(datapointTables.length)}
            >
                {datapointTables.map(_ => tableToNode(_))}
            </CopyableTreeNode>

            <CopyableTreeNode
                itemId="events"
                key="events"
                label="Event Tables"
                badge={String(eventTables.length)}
            >
                {eventTables.map(_ => tableToNode(_))}
            </CopyableTreeNode>

            <CopyableTreeNode
                itemId="entity"
                key="entity"
                label="Entity Tables"
                badge={String(entityTables.length)}
            >
                {entityTables.map(_ => tableToNode(_))}
            </CopyableTreeNode>
        </TreeViewStyled>
    );
}

export function SqlTableHelper() {
    const { isOpen, openDropdown, closeDropdown } = usePopup<HTMLButtonElement>(
        {
            placement: "bottom-start",
            modifiers: [{ name: "offset", options: { offset: [-100, 10] } }],
        },
    );

    return (
        <>
            <ExpandButton
                size="24"
                onClick={() => (!isOpen ? openDropdown() : closeDropdown())}
                icon="table"
            />
            <Popup visible={isOpen} variant="light" onClose={closeDropdown}>
                <SqlTableViewer />
            </Popup>
        </>
    );
}

const snowflakeTables = {
    streams: [
        "NUMERIC",
        "NUMERIC_POINTS",
        "NUMERIC_TAGS",
        "BATTERY",
        "BATTERY_POINTS",
        "BATTERY_TAGS",
        "BITSET",
        "BITSET_POINTS",
        "BITSET_POINTS_1_KEYS",
        "BITSET_POINTS_1_VALUES",
        "BITSET_TAGS",
        "JSON",
        "JSON_POINTS",
        "JSON_TAGS",
        "LOCATION",
        "LOCATION_POINTS",
        "LOCATION_TAGS",
    ],
    events: [
        "EVENTS",
        "EVENTS_TAGS",
        "INTERVENTION_REQUEST",
        "INTERVENTION_RESPONSE",
        "LIST_INTERVENTION_REQUESTS",
        "LIST_INTERVENTION_REQUESTS_OPTIONS",
        "LIST_INTERVENTION_REQUESTS_RESPONSES",
    ],
    entities: [
        "DEVICES",
        "DEVICES_QUERY_TAGS",
        "USERS",
        "USERS_TAGS",
        "KEY_VALS",
        "KEY_VALS_TAGS",
    ],
};

const clickhouseTables = {
    streams: [
        "numeric_set",
        "battery",
        "bitset",
        "text",
        "health",
        "json",
        "numeric",
        "location",
    ],
    events: [
        "annotation",
        "command_delivery",
        "command_request",
        "command_response",
        "intervention_request",
        "intervention_response",
        "comment",
        "custom_event",
        "datapoint_event",
        "event",
        "port_forwarding_session_record",
        "system_event",
        "task_summary",
        "teleop_session_record",
    ],
    entities: [
        "mapping_account",
        "mapping_device",
        "mapping_fleet",
        "mapping_organization",
        "mapping_team",
        "mapping_user",
    ],
};

export const ExpandButton = styled(IconButton)`
    padding: 0.425rem;
    padding-top: 0.3rem;
    border-radius: 1rem;
`;

export const TreeViewStyled = styled(TreeView)`
    flex-grow: 1px;
    margin-top: 1rem;
    max-width: 800px;
    min-height: 200px;

    overflow-y: "auto";
    padding: 0.25rem;
    max-height: 40rem;
    overflow-y: scroll;

    .MuiTreeItem-root,
    .Mui-selected,
    .Mui-focused {
        background-color: ${primary.module} !important;
    }

    .MuiTreeItem-label {
        min-width: 18rem;
    }
`;

const sqlColumnToIcon: Record<
    "string" | "number" | "boolean" | "timestamp" | "null",
    IconName
> = {
    string: "text",
    number: "scatter",
    boolean: "bitset",
    timestamp: "clock",
    null: "scatter",
};

const columnToIcon = (
    input?: "string" | "number" | "boolean" | "timestamp" | "null",
): IconName => {
    return sqlColumnToIcon[input || "number"] ?? "scatter";
};
